/**
 * Compare Plans Page
 * Compare Free, Economy, Premium, Elite – Quarterly vs Annually
 * iOS-first, consistent with MembershipPage
 *
 * @author Varun Pandey
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitCompare, Check, X, ChevronDown, ChevronRight, Star, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import {
  COMPARE_PLANS,
  COMPARE_SECTIONS,
  getCompareCell,
  getCompareEmiForCategory,
  getComparePricesForCategory,
  getIosComparePricesForCategory,
  type BillingPeriod,
  type CellValue,
  type PlanKey,
} from '@/data/comparePlansData';
import {
  getMembershipPlansForCategory,
  getPlanDisplayPrice,
  MEMBERSHIP_PLANS,
} from '@/data/membershipPlansData';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import { isNativeIOSApp } from '@/utils/platform';
import { getRcPackageIdForBackendPlan } from '@/data/iapProductMapping';
import { useIosSubscriptionPrices } from '@/hooks/useIosSubscriptionPrices';
import { useRevenueCatAppUserId } from '@/hooks/useRevenueCatAppUserId';
import {
  membershipCheckoutUsesIap,
  membershipEmiAvailable,
} from '@/services/membershipPaymentService';
import type { IosPackagePriceMap } from '@/services/revenueCatService';
import { resolveRevenueCatAppUserId } from '@/utils/revenueCatAppUserId';

function Cell({ value }: { value: CellValue }) {
  if (typeof value === 'boolean') {
    return value ? <Check className="h-5 w-5 text-emerald-500 shrink-0" /> : <X className="h-5 w-5 text-[#636366] shrink-0" />;
  }
  return <span className="text-[#E5E5EA] text-xs lg:text-sm font-medium text-center">{value || '—'}</span>;
}

function modIndex(n: number, length: number) {
  return ((n % length) + length) % length;
}

function comparePlanKeyToMembership(
  plan: PlanKey,
  membershipPlans: ReturnType<typeof getMembershipPlansForCategory>,
) {
  if (plan === 'Free') return null;
  return membershipPlans.find((p) => p.name === plan) ?? null;
}

interface CompareHeaderPriceProps {
  plan: PlanKey;
  billingPeriod: BillingPeriod;
  categoryName?: string | null;
  usesAppleIap: boolean;
  iosPrices?: IosPackagePriceMap;
  iosPricesLoading?: boolean;
  comparePrices: ReturnType<typeof getComparePricesForCategory>;
  membershipPlans: ReturnType<typeof getMembershipPlansForCategory>;
}

function CompareHeaderPrice({
  plan,
  billingPeriod,
  categoryName,
  usesAppleIap,
  iosPrices,
  iosPricesLoading = false,
  comparePrices,
  membershipPlans,
}: CompareHeaderPriceProps) {
  if (plan === 'Free') {
    return <p className="text-base lg:text-lg font-bold text-white mt-0.5">₹0</p>;
  }

  const period = billingPeriod === 'annually' ? 'annually' : 'quarterly';
  const membershipPlan =
    comparePlanKeyToMembership(plan, membershipPlans) ??
    MEMBERSHIP_PLANS.find((p) => p.name === plan) ??
    null;

  const renderInrPrice = (amount: number) => (
    <p className="text-base lg:text-lg font-bold text-white mt-0.5">
      ₹{amount.toLocaleString()}
    </p>
  );

  if (!usesAppleIap) {
    if (membershipPlan) {
      return renderInrPrice(getPlanDisplayPrice(membershipPlan, period, categoryName));
    }
    return (
      <p className="text-base lg:text-lg font-bold text-white mt-0.5">
        {comparePrices[billingPeriod][plan]}
      </p>
    );
  }

  if (!membershipPlan) {
    return (
      <p className="text-base lg:text-lg font-bold text-white mt-0.5">
        {comparePrices[billingPeriod][plan]}
      </p>
    );
  }
  const backendPlanId =
    period === 'annually' ? membershipPlan.annualPlanId : membershipPlan.quarterlyPlanId;
  const rcPackageId = backendPlanId
    ? getRcPackageIdForBackendPlan(backendPlanId, categoryName)
    : null;
  const applePrice = rcPackageId && iosPrices ? iosPrices[rcPackageId] : undefined;

  if (iosPricesLoading && !applePrice) {
    return (
      <span
        className="inline-block h-5 w-20 rounded bg-[#2C2C2E] animate-pulse mt-0.5"
        aria-hidden
      />
    );
  }

  if (applePrice) {
    return (
      <p className="text-base lg:text-lg font-bold text-white mt-0.5">{applePrice}</p>
    );
  }

  return renderInrPrice(getPlanDisplayPrice(membershipPlan, period, categoryName));
}

function getFontFamilyForMeasure() {
  if (typeof document === 'undefined') return 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
  const fam = window.getComputedStyle(document.body).fontFamily;
  return fam || 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
}

function measureTextPx(text: string, font: string, letterSpacingPx: number) {
  if (!text) return 0;
  if (typeof document === 'undefined') {
    // Fallback: approx 0.62em per char + letter spacing
    const fontSizeMatch = font.match(/(\d+(?:\.\d+)?)px/);
    const fontSize = fontSizeMatch ? Number(fontSizeMatch[1]) : 12;
    return text.length * fontSize * 0.62 + Math.max(0, text.length - 1) * letterSpacingPx;
  }
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return text.length * 7;
  ctx.font = font;
  const base = ctx.measureText(text).width;
  return base + Math.max(0, text.length - 1) * letterSpacingPx;
}

function sliceHeadToFit(text: string, maxPx: number, font: string, letterSpacingPx: number) {
  if (!text) return '';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const sub = text.slice(0, mid);
    if (measureTextPx(sub, font, letterSpacingPx) <= maxPx) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo);
}

function sliceTailToFit(text: string, maxPx: number, font: string, letterSpacingPx: number) {
  if (!text) return '';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const sub = text.slice(text.length - mid);
    if (measureTextPx(sub, font, letterSpacingPx) <= maxPx) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(text.length - lo);
}

function QuarterCircleSectionPicker({
  labels,
  selectedIndex,
  onChange,
}: {
  labels: string[];
  selectedIndex: number;
  onChange: (nextIndex: number) => void;
}) {
  const [isShown, setIsShown] = useState(false);
  const lastScrollTopRef = useRef(0);
  const scrollElRef = useRef<HTMLElement | null>(null);

  const r = 200;
  const borderPad = 14;
  const arcInset = 32; // how far inside the circle the curved labels sit
  const rr = r - arcInset; // radius for the text arc (inside the border)
  const arcLen = (Math.PI / 2) * rr;
  const gapPx = 10;
  const hintInset = arcInset + 18; // swipe hint sits below the text arc
  const rHintClockwise = r - hintInset;
  const rHintAnti = rHintClockwise - 12;

  const points = useMemo(() => {
    const left = { x: 0, y: r };
    const top = { x: r, y: 0 };
    const mid = { x: r - r / Math.SQRT2, y: r - r / Math.SQRT2 };
    return { left, top, mid };
  }, [r]);

  const arcPaths = useMemo(() => {
    const cx = r;
    const cy = r;
    const polar = (deg: number) => {
      const rad = (deg * Math.PI) / 180;
      return {
        x: cx + rr * Math.cos(rad),
        y: cy + rr * Math.sin(rad),
      };
    };
    const polarHint = (deg: number, radius: number) => {
      const rad = (deg * Math.PI) / 180;
      return {
        x: cx + radius * Math.cos(rad),
        y: cy + radius * Math.sin(rad),
      };
    };
    const arc = (startDeg: number, endDeg: number) => {
      const s = polar(startDeg);
      const e = polar(endDeg);
      // sweepFlag=1 matches our quadrant arc direction
      return `M ${s.x} ${s.y} A ${rr} ${rr} 0 0 1 ${e.x} ${e.y}`;
    };
    const arcHint = (startDeg: number, endDeg: number, radius: number) => {
      const s = polarHint(startDeg, radius);
      const e = polarHint(endDeg, radius);
      return `M ${s.x} ${s.y} A ${radius} ${radius} 0 0 1 ${e.x} ${e.y}`;
    };

    // Quarter arc angles:
    // - left point is 180°
    // - top point is 270°
    // We intentionally give the SELECTED label a larger arc segment so long
    // titles (e.g. "ADVERTISEMENT") don't get clipped.
    const a0 = 180;
    const a3 = 270;
    // Segment sizes (degrees): prev 18°, selected 44°, next 28°
    const a1 = 198;
    const a2 = 242;

    return {
      all: arc(a0, a3),
      prev: arc(a0, a1),
      selected: arc(a1, a2),
      next: arc(a2, a3),
      // swipe hints (stacked, below the text arc)
      // Upper = clockwise (left -> top), Lower = anti-clockwise (top -> left)
      hintClockwise: arcHint(208, 258, rHintClockwise),
      // Draw same direction/shape; arrow placement indicates direction.
      hintAnti: arcHint(208, 258, rHintAnti),
    };
  }, [r, rHintAnti, rHintClockwise, rr]);

  const len = labels.length;
  const prevIndex = modIndex(selectedIndex - 1, len);
  const nextIndex = modIndex(selectedIndex + 1, len);

  useEffect(() => {
    // Slide in on mount
    const t = window.setTimeout(() => setIsShown(true), 80);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const el = (document.querySelector('main.content-safe-scroll') as HTMLElement | null) ?? null;
    scrollElRef.current = el;
    if (!el) return;

    lastScrollTopRef.current = el.scrollTop;

    const onScroll = () => {
      const current = el.scrollTop;
      const prev = lastScrollTopRef.current;
      const delta = current - prev;

      // Ignore tiny deltas to avoid flicker
      if (Math.abs(delta) > 6) {
        if (delta > 0) setIsShown(false); // scrolling down -> hide
        else setIsShown(true); // scrolling up -> show
        lastScrollTopRef.current = current;
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll as EventListener);
  }, []);

  const arcTextLayout = useMemo(() => {
    const family = getFontFamilyForMeasure();
    const prevFontSize = 11.5;
    const prevWeight = 600;
    const prevLetterSpacingPx = prevFontSize * 0.06;
    const prevFont = `${prevWeight} ${prevFontSize}px ${family}`;

    const selFontSize = 12.5;
    const selWeight = 800;
    const selLetterSpacingPx = selFontSize * 0.08;
    const selFont = `${selWeight} ${selFontSize}px ${family}`;

    const prevLabel = String(labels[prevIndex] ?? '').toUpperCase();
    const selLabel = String(labels[selectedIndex] ?? '').toUpperCase();
    const nextLabel = String(labels[nextIndex] ?? '').toUpperCase();

    const selPx = measureTextPx(selLabel, selFont, selLetterSpacingPx);
    const safeSelPx = Math.min(selPx, Math.max(arcLen - 2 * gapPx, arcLen * 0.92));
    const remaining = Math.max(0, arcLen - safeSelPx - 2 * gapPx);
    const sideMax = remaining / 2;

    const prevShown = sliceTailToFit(prevLabel, sideMax, prevFont, prevLetterSpacingPx);
    const nextShown = sliceHeadToFit(nextLabel, sideMax, prevFont, prevLetterSpacingPx);

    const centerStartPx = arcLen / 2 - safeSelPx / 2;
    const centerEndPx = arcLen / 2 + safeSelPx / 2;

    const prevCenterPx = Math.max(gapPx, centerStartPx - gapPx - sideMax / 2);
    const nextCenterPx = Math.min(arcLen - gapPx, centerEndPx + gapPx + sideMax / 2);

    const pct = (px: number) => `${(Math.max(0, Math.min(arcLen, px)) / arcLen) * 100}%`;

    return {
      prevShown,
      selLabel,
      nextShown,
      prevStartOffset: pct(prevCenterPx),
      selStartOffset: '50%',
      nextStartOffset: pct(nextCenterPx),
    };
  }, [arcLen, gapPx, labels, nextIndex, prevIndex, selectedIndex]);

  const startRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);

  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
    // Only track the primary finger / primary button.
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    setIsShown(true);
    startRef.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerUp: React.PointerEventHandler<HTMLDivElement> = (e) => {
    const start = startRef.current;
    if (!start) return;
    if (start.pointerId !== e.pointerId) return;
    startRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);

    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;

    // Tolerant curved-swipe detection:
    // clockwise vector ~= (1, -1), anti-clockwise ~= (-1, 1).
    const travel = Math.hypot(dx, dy);
    if (travel < 24) return;
    const directionalScore = dx - dy;
    if (directionalScore > 16) {
      onChange(prevIndex);
      return;
    }
    if (directionalScore < -16) {
      onChange(nextIndex);
    }
  };

  const handlePointerCancel: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (startRef.current?.pointerId === e.pointerId) {
      startRef.current = null;
    }
  };

  const baseBottom = 'calc(128px + env(safe-area-inset-bottom, 0px) + 8px)';

  return (
    <motion.div
      className="fixed right-0 z-[2] lg:hidden touch-none select-none"
      style={{ bottom: baseBottom, touchAction: 'none' }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      // Hidden state keeps only a tiny curve "peek" visible bottom-right.
      initial={{ x: r - 120, y: r - 120, scale: 0.84, opacity: 1 }}
      animate={isShown ? { x: 0, y: 60, scale: 1, opacity: 1 } : { x: r - 120, y: r - 60, scale: 0.84, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 520, damping: 34 }}
    >
      <div className="relative overflow-hidden" style={{ width: r, height: r }}>
        {/* Full circle, clipped to show only the top-left quadrant */}
        <div
          className="absolute"
          style={{
            width: r * 2,
            height: r * 2,
            right: -r,
            bottom: -r,
          }}
        >
          <div
            className={cn(
              'w-full h-full rounded-full',
              'bg-[#1C1C1E]/85 backdrop-blur-md',
              'border border-[#3A3A3C]/70 '
            )}
          />
        </div>

        {/* Curved labels along the arc border */}
        <svg
          className="absolute inset-0"
          width={r}
          height={r}
          viewBox={`0 0 ${r} ${r}`}
          aria-hidden="true"
        >
          <defs>
            <path id="compare-wheel-arc-all" d={arcPaths.all} />
            <path id="compare-wheel-arc-prev" d={arcPaths.prev} />
            <path id="compare-wheel-arc-selected" d={arcPaths.selected} />
            <path id="compare-wheel-arc-next" d={arcPaths.next} />

            {/* Brand pink gradient for selected label */}
            <linearGradient id="compare-wheel-selected-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FF4D79" />
              <stop offset="55%" stopColor="#F33363" />
              <stop offset="100%" stopColor="#B71D49" />
            </linearGradient>

            {/* Arrowhead markers for swipe hints */}
            <marker
              id="compare-wheel-arrow"
              viewBox="0 0 10 10"
              refX="8.5"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(142,142,147,0.55)" />
            </marker>

            <marker
              id="compare-wheel-arrow-rev"
              viewBox="0 0 10 10"
              refX="8.5"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(142,142,147,0.55)" />
            </marker>
          </defs>

          <path
            d={arcPaths.prev}
            fill="none"
            stroke="transparent"
            strokeWidth={44}
            onClick={() => onChange(prevIndex)}
            style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
          />
          <path
            d={arcPaths.selected}
            fill="none"
            stroke="transparent"
            strokeWidth={44}
            onClick={() => onChange(selectedIndex)}
            style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
          />
          <path
            d={arcPaths.next}
            fill="none"
            stroke="transparent"
            strokeWidth={44}
            onClick={() => onChange(nextIndex)}
            style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
          />

          {/* Previous (near left) - on inner arc */}
          <text
            fill="#8E8E93"
            fontSize="11.5"
            fontWeight="600"
            letterSpacing="0.06em"
            style={{ textTransform: 'uppercase', userSelect: 'none', cursor: 'pointer' }}
            onClick={() => onChange(prevIndex)}
          >
            <textPath href="#compare-wheel-arc-all" startOffset={arcTextLayout.prevStartOffset} textAnchor="middle">
              {arcTextLayout.prevShown}
            </textPath>
          </text>

          {/* Selected (middle segment of arc) */}
          <text
            fill="url(#compare-wheel-selected-gradient)"
            fontSize="12.5"
            fontWeight="800"
            letterSpacing="0.08em"
            style={{ textTransform: 'uppercase', userSelect: 'none', cursor: 'pointer' }}
            onClick={() => onChange(selectedIndex)}
          >
            <textPath href="#compare-wheel-arc-all" startOffset={arcTextLayout.selStartOffset} textAnchor="middle">
              {arcTextLayout.selLabel}
            </textPath>
          </text>

          {/* Next (near top) */}
          <text
            fill="#8E8E93"
            fontSize="11.5"
            fontWeight="600"
            letterSpacing="0.06em"
            style={{ textTransform: 'uppercase', userSelect: 'none', cursor: 'pointer' }}
            onClick={() => onChange(nextIndex)}
          >
            <textPath href="#compare-wheel-arc-all" startOffset={arcTextLayout.nextStartOffset} textAnchor="middle">
              {arcTextLayout.nextShown}
            </textPath>
          </text>

          {/* Swipe hint arcs (subtle, below text arc) */}
          <motion.path
            d={arcPaths.hintClockwise}
            fill="none"
            stroke="rgba(142,142,147,0.45)"
            strokeWidth={2}
            strokeLinecap="round"
            markerEnd="url(#compare-wheel-arrow)"
            initial={false}
            animate={{ opacity: isShown ? 1 : 0 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
          />
          <motion.path
            d={arcPaths.hintAnti}
            fill="none"
            stroke="rgba(142,142,147,0.28)"
            strokeWidth={2}
            strokeLinecap="round"
            markerStart="url(#compare-wheel-arrow-rev)"
            initial={false}
            animate={{ opacity: isShown ? 1 : 0 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
          />
        </svg>

        {/* Helper caption */}
        <div
          className="absolute left-1/2 bottom-3 -translate-x-1/2 text-[11px] font-semibold uppercase tracking-wide text-[#8E8E93] select-none pointer-events-none"
          style={{ textShadow: '0 1px 12px rgba(0,0,0,0.55)' }}
        >
          Swipe ↻ / ↺
        </div>

        {/* Center label (kept inside the quadrant) */}
        {/* <button
          type="button"
          onClick={() => onChange(selectedIndex)}
          className={cn(
            'absolute select-none whitespace-nowrap',
            'text-[13px] font-bold uppercase tracking-widest',
            'text-white'
          )}
          style={{
            left: points.mid.x + borderPad,
            top: points.mid.y + borderPad,
            transform: 'translate(-50%, -50%)',
          }}
        >
          {labels[selectedIndex]}
        </button> */}
      </div>
    </motion.div>
  );
}

export const ComparePlansPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: artistDetails } = useArtistDetailsStore();
  const artistCategoryName = artistDetails?.artist_category?.name ?? null;
  const membershipPlans = getMembershipPlansForCategory(artistCategoryName);
  const comparePrices = isNativeIOSApp()
    ? getIosComparePricesForCategory(artistCategoryName)
    : getComparePricesForCategory(artistCategoryName);
  const compareEmi = getCompareEmiForCategory(artistCategoryName);

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annually');
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [expandedSection, setExpandedSection] = useState<string | null>(COMPARE_SECTIONS[0].id);

  const usesAppleIap = membershipCheckoutUsesIap();
  const revenueCatAppUserId = useRevenueCatAppUserId();
  const { data: iosPrices, isLoading: iosPricesLoading } =
    useIosSubscriptionPrices(revenueCatAppUserId);

  useEffect(() => {
    if (usesAppleIap) {
      void resolveRevenueCatAppUserId();
    }
  }, [usesAppleIap]);

  const handleBuyNow = () => {
    navigate('/membership');
  };

  const activeSection = COMPARE_SECTIONS[modIndex(selectedSectionIndex, COMPARE_SECTIONS.length)];

  return (
    <div className="w-full min-h-screen bg-[#0B0B0D] text-white">
      <div className="w-full md:px-4 py-10 pb-24">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 lg:h-12 lg:w-12 shrink-0 items-center justify-center rounded-xl bg-[#1C1C1E] border border-[#3A3A3C]">
              <GitCompare className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93]" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight uppercase tracking-widest">
                Compare plans
              </h1>
              <p className="text-[#8E8E93] text-sm lg:text-base mt-0.5">
                Compare features across Free, Economy, Premium & Elite
              </p>
            </div>
          </div>
        </div>

        {/* Billing Period Tabs + Back to plans – same row as MembershipPage */}
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4 border-b border-[#3A3A3C] pb-0">
          <nav className="flex gap-6 lg:gap-8" aria-label="Billing period">
            <button
              type="button"
              onClick={() => setBillingPeriod('quarterly')}
              className={cn(
                'relative pb-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm',
                billingPeriod === 'quarterly'
                  ? 'text-white'
                  : 'text-[#8E8E93] hover:text-white'
              )}
              aria-current={billingPeriod === 'quarterly' ? 'page' : undefined}
            >
              Quarterly
              {billingPeriod === 'quarterly' && (
                <motion.span
                  layoutId="compare-tab-underline"
                  className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#F33363] rounded-full"
                  transition={{ type: 'spring', duration: 0.3 }}
                />
              )}
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod('annually')}
              className={cn(
                'relative pb-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm',
                billingPeriod === 'annually'
                  ? 'text-white'
                  : 'text-[#8E8E93] hover:text-white'
              )}
              aria-current={billingPeriod === 'annually' ? 'page' : undefined}
            >
              Annually
              {billingPeriod === 'annually' && (
                <motion.span
                  layoutId="compare-tab-underline"
                  className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#F33363] rounded-full"
                  transition={{ type: 'spring', duration: 0.3 }}
                />
              )}
            </button>
          </nav>
          <button
            type="button"
            onClick={() => navigate('/membership')}
            className="inline-flex items-center gap-1.5 py-2 px-4 rounded-br-none text-sm font-medium border border-[#3A3A3C] text-[#8E8E93] hover:text-[#F33363] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to plans
          </button>
        </div>

        {/* Pricing card: same 5-column grid as comparison rows so plan columns align */}
        <motion.div
          key={billingPeriod}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="rounded-2xl border border-[#3A3A3C] bg-[#1C1C1E] overflow-hidden mb-6"
        >
          <div className="grid grid-cols-4 gap-0 min-w-0">
            {COMPARE_PLANS.map((plan) => {
              const emi = compareEmi[billingPeriod][plan];
              return (
                <div
                  key={plan}
                  className="p-4 border-b border-l first:border-l-0 border-[#3A3A3C]/50 text-center min-w-0"
                >
                  <p className="text-xs lg:text-sm font-semibold text-[#8E8E93] uppercase tracking-wide">{plan}</p>
                  <CompareHeaderPrice
                    plan={plan}
                    billingPeriod={billingPeriod}
                    categoryName={artistCategoryName}
                    usesAppleIap={usesAppleIap}
                    iosPrices={iosPrices}
                    iosPricesLoading={iosPricesLoading}
                    comparePrices={comparePrices}
                    membershipPlans={membershipPlans}
                  />
                  {emi != null && membershipEmiAvailable() && (
                    <p className="text-xs text-[#8E8E93] mt-0.5 font-medium">EMI from ₹{emi.toLocaleString()}/mo</p>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Mobile: single active section (selected via quarter-circle picker) */}
        <motion.div
          key={`${billingPeriod}-${activeSection.id}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.18 }}
          className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden lg:hidden"
        >
          <div className="px-4 py-4 border-b border-[#3A3A3C]/50">
            <div className="flex items-center justify-between gap-3">
              <span className="text-base font-semibold text-white">{activeSection.title}</span>
              <span className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wide">Swipe / tap selector</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div
              className={cn(
                'grid grid-cols-[minmax(180px,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-0 min-w-[500px]',
                'border-b border-[#3A3A3C]/50 bg-[#252527]/50'
              )}
            >
              <div className="py-3 px-4 text-xs font-semibold text-[#8E8E93] uppercase tracking-wide text-left">
                Benefit
              </div>
              {COMPARE_PLANS.map((plan) => (
                <div key={plan} className="py-3 px-2 text-xs font-semibold text-[#8E8E93] uppercase tracking-wide text-center min-w-0">
                  {plan}
                </div>
              ))}
            </div>
            {activeSection.rows.map((row, idx) => (
              <div
                key={idx}
                className={cn(
                  'grid grid-cols-[minmax(180px,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-0 min-w-[500px] items-center min-h-[48px]',
                  'border-b border-[#3A3A3C]/30 last:border-b-0',
                  idx % 2 === 1 && 'bg-[#252527]/30'
                )}
              >
                <div className="py-2.5 px-4 text-sm text-[#E5E5EA] font-medium text-left">
                  {row.label}
                </div>
                {COMPARE_PLANS.map((plan) => (
                  <div key={plan} className="py-2.5 px-2 flex justify-center items-center min-w-0">
                    <Cell value={getCompareCell(row, plan, billingPeriod)} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Desktop: original accordion */}
        <div className="hidden lg:block space-y-2">
          {COMPARE_SECTIONS.map((section) => {
            const isExpanded = expandedSection === section.id;
            return (
              <motion.div
                key={section.id}
                initial={false}
                className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-4 lg:px-5 lg:py-4 min-h-[56px] hover:bg-[#252527] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-inset"
                >
                  <span className="text-base font-semibold text-white">{section.title}</span>
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-[#8E8E93] shrink-0" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-[#8E8E93] shrink-0" />
                  )}
                </button>
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-[#3A3A3C]/50 overflow-x-auto">
                        <div
                          className={cn(
                            'grid grid-cols-[minmax(180px,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-0 min-w-[500px]',
                            'border-b border-[#3A3A3C]/50 bg-[#252527]/50'
                          )}
                        >
                          <div className="py-3 px-4 text-xs font-semibold text-[#8E8E93] uppercase tracking-wide text-left">
                            Benefit
                          </div>
                          {COMPARE_PLANS.map((plan) => (
                            <div
                              key={plan}
                              className="py-3 px-2 text-xs font-semibold text-[#8E8E93] uppercase tracking-wide text-center min-w-0"
                            >
                              {plan}
                            </div>
                          ))}
                        </div>
                        {section.rows.map((row, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              'grid grid-cols-[minmax(180px,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-0 min-w-[500px] items-center min-h-[48px]',
                              'border-b border-[#3A3A3C]/30 last:border-b-0',
                              idx % 2 === 1 && 'bg-[#252527]/30'
                            )}
                          >
                            <div className="py-2.5 px-4 text-sm text-[#E5E5EA] font-medium text-left">
                              {row.label}
                            </div>
                            {COMPARE_PLANS.map((plan) => (
                              <div key={plan} className="py-2.5 px-2 flex justify-center items-center min-w-0">
                                <Cell value={getCompareCell(row, plan, billingPeriod)} />
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Buy Now */}
        <div className="mt-8 flex justify-center">
          <Button variant="active" className="min-h-[48px] px-8" onClick={handleBuyNow}>
            <Star className="h-4 w-4" />
            Buy plan on Membership
          </Button>
        </div>
      </div>

      <QuarterCircleSectionPicker
        labels={COMPARE_SECTIONS.map((s) => s.title)}
        selectedIndex={modIndex(selectedSectionIndex, COMPARE_SECTIONS.length)}
        onChange={(next) => setSelectedSectionIndex(modIndex(next, COMPARE_SECTIONS.length))}
      />
    </div>
  );
};
